// Line-count audit (2026-05-04): 158 lines.
import { componentReferenceResolver } from '@/services/ai/ComponentReferenceResolver';
import { Component, ComponentType } from '@/types';

function collectDescendants(rootId: string, components: Component[]): Component[] {
  const descendants: Component[] = [];
  const queue = components.filter((component) => component.parentId === rootId);

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }

    descendants.push(current);
    queue.push(...components.filter((component) => component.parentId === current.id));
  }

  return descendants;
}

export function resolveScopedChildGroup(
  command: string,
  components: Component[],
  selectedComponents: Component[],
  pageId?: string | null
): { targets: Component[]; actionCommand: string; targetLabel: string } | null {
  const scopedChildMatch = command.match(
    /\b(icons?)\s+(?:on|in|inside|within)\s+(?:the\s+)?([a-z0-9][a-z0-9\s_-]*?)(?:[?.!]|$)/i
  );

  if (!scopedChildMatch) {
    return null;
  }

  const [, childReferenceRaw, scopeReferenceRaw] = scopedChildMatch;
  const childReference = childReferenceRaw.toLowerCase().startsWith('icon')
    ? 'icon'
    : childReferenceRaw.toLowerCase();
  const scopeReference = scopeReferenceRaw.trim();

  const scopeResolution = componentReferenceResolver.resolveSemanticReference(scopeReference, {
    selectedComponents,
    pageId: pageId || undefined,
  });

  const scopeCandidates = scopeResolution.allMatches
    .map((match) => components.find((component) => component.id === match.id))
    .filter((component): component is Component => component !== undefined);

  if (scopeCandidates.length === 0) {
    return null;
  }

  const matchedTargets = new Map<string, Component>();
  for (const scopeCandidate of scopeCandidates) {
    const descendants = collectDescendants(scopeCandidate.id, components);
    for (const descendant of descendants) {
      const searchable = `${descendant.id} ${descendant.name || ''} ${descendant.type}`.toLowerCase();
      if (childReference === 'icon' && (
        descendant.type === ComponentType.ICON ||
        searchable.includes('icon')
      )) {
        matchedTargets.set(descendant.id, descendant);
      }
    }
  }

  const targets = Array.from(matchedTargets.values());
  if (targets.length < 2) {
    return null;
  }

  let actionCommand = command.replace(scopedChildMatch[0], childReference);
  actionCommand = actionCommand
    .replace(/^(?:can|could|would)\s+(?:you|we)\s+/i, '')
    .replace(/^please\s+/i, '')
    .replace(/[?.!]+$/, '')
    .trim();

  if (/^center\b/i.test(actionCommand) && !/\bicon\b/i.test(actionCommand)) {
    actionCommand = 'center icon';
  }

  return {
    targets,
    actionCommand,
    targetLabel: `${childReferenceRaw} on ${scopeReference}`,
  };
}

export function resolveScopedParentContainer(
  command: string,
  components: Component[]
): { target: Component; actionCommand: string; targetLabel: string } | null {
  const lowerCommand = command.toLowerCase();
  const mentionsContainerIntent =
    /\b(container|above and below|vertical spacing|empty space|less empty space)\b/i.test(command);
  const groupMatch = command.match(/\b(kpi\s+cards?|cards?)\b/i);

  if (!mentionsContainerIntent || !groupMatch) {
    return null;
  }

  const groupReference = groupMatch[1];
  const candidateCards = components.filter((component) => {
    const semanticRoleValue = component.properties?._semanticRole;
    const semanticRole = typeof semanticRoleValue === 'string' ? semanticRoleValue.toLowerCase() : '';
    const searchable = `${component.id} ${component.name || ''} ${component.type} ${semanticRole}`.toLowerCase();

    return (
      component.type === ComponentType.CARD ||
      component.type === ComponentType.DATA_CARD ||
      searchable.includes('card') ||
      searchable.includes('kpi') ||
      searchable.includes('metric') ||
      searchable.includes('stat')
    );
  });

  if (candidateCards.length < 2) {
    return null;
  }

  const parentFrequency = new Map<string, number>();
  for (const card of candidateCards) {
    if (!card.parentId) {
      continue;
    }
    parentFrequency.set(card.parentId, (parentFrequency.get(card.parentId) || 0) + 1);
  }

  const winningParentId = Array.from(parentFrequency.entries())
    .sort((left, right) => right[1] - left[1])[0]?.[0];
  if (!winningParentId) {
    return null;
  }

  const parentComponent = components.find((component) => component.id === winningParentId);
  if (!parentComponent) {
    return null;
  }

  let actionCommand: string | null = null;
  if (/\b(vertical spacing|above and below|less empty space|empty space)\b/i.test(command)) {
    actionCommand = /\b(increase|more)\b/i.test(lowerCommand)
      ? 'increase vertical padding'
      : 'decrease vertical padding';
  } else if (/\bcontainer\b/i.test(command) && /\b(padding|space|spacing)\b/i.test(command)) {
    actionCommand = /\b(increase|more)\b/i.test(lowerCommand)
      ? 'increase vertical padding'
      : 'decrease vertical padding';
  }

  return actionCommand
    ? { target: parentComponent, actionCommand, targetLabel: `${groupReference} container` }
    : null;
}
